import { createRouteRef, createApiRef, createPlugin, createApiFactory, githubAuthApiRef, errorApiRef, createRoutableExtension, createComponentExtension, useApi, SessionState, useApiHolder } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, Grid, Avatar, Typography, Link, Box, Divider, Tooltip, Chip, Button, List, ListItem } from '@material-ui/core';
import { Progress, MissingAnnotationEmptyState, InfoCard, MarkdownContent as MarkdownContent$2, StructuredMetadataTable, Page, Content, ContentHeader, SupportButton } from '@backstage/core-components';
import { withStyles, makeStyles as makeStyles$1 } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { ANNOTATION_SOURCE_LOCATION, ANNOTATION_LOCATION, parseLocationRef } from '@backstage/catalog-model';
import { useAsync } from 'react-use';
import { useEntity } from '@backstage/plugin-catalog-react';
import create from 'zustand';
import produce from 'immer';
import { Alert as Alert$1 } from '@material-ui/lab';
import useAsync$1 from 'react-use/lib/useAsync';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import WarningIcon from '@material-ui/icons/ErrorOutline';

const rootRouteRef = createRouteRef({
  id: "code-insights"
});

const mimeTypeMap = {
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp"
};
const mimeTypeLookup = (href) => {
  const match = href.match(/\.([a-zA-Z]*)$/);
  const extension = match ? match[1] : void 0;
  return extension ? mimeTypeMap[extension.toLowerCase()] : void 0;
};
const getRepositoryDefaultBranch = (url) => {
  return new URL(url).searchParams.get("ref");
};
const defaultBaseUrl = "https://api.github.com";
class GithubClient {
  constructor(deps) {
    this.githubAuthApi = deps.githubAuthApi;
    this.errorApi = deps.errorApi;
  }
  async getContent(props) {
    const { path, repo, owner, branch, baseUrl = defaultBaseUrl } = props;
    const token = await this.githubAuthApi.getAccessToken();
    const octokit = new Octokit({ auth: token, baseUrl });
    let query = "readme";
    if (path) {
      query = `contents/${path}`;
    }
    const response = await octokit.request(
      `GET /repos/{owner}/{repo}/${query}`,
      {
        owner,
        repo,
        ...branch && { ref: branch }
      }
    );
    const content = Buffer.from(response.data.content, "base64").toString(
      "utf8"
    );
    const mediaLinks = [
      ...content.matchAll(
        /\[([^\[\]]*)\]\((.*?)(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.svg)(.*)\)/gim
      )
    ].map((match) => [match[2], match[3]].join(""));
    const media = {};
    for (const href of mediaLinks) {
      const mimeType = mimeTypeLookup(href);
      const url = new URL(href, response.data.url);
      if (mimeType && url.host.includes("github.com")) {
        const requestPath = url.pathname.replace(
          new RegExp(`/${owner}/${repo}/blob/(main|master)`),
          `/repos/${owner}/${repo}/contents`
        );
        try {
          const contentResponse = await octokit.request(`GET ${requestPath}`);
          media[href] = `data:${mimeType};base64,${contentResponse.data.content.replaceAll(
            "\n",
            ""
          )}`;
        } catch (e) {
          this.errorApi.post(e);
        }
      }
    }
    const markdownLinks = [
      ...content.matchAll(/\[([^\[\]]*)\]\((?!https?:\/\/)(.*?)(\.md)\)/gim)
    ].map((match) => [match[2], match[3]].join(""));
    const links = {};
    for (const markdownLink of markdownLinks) {
      links[markdownLink] = `https://github.com/${owner}/${repo}/blob/${branch || getRepositoryDefaultBranch(response.data.url)}/${markdownLink}`;
    }
    return { content, media, links };
  }
}

const githubApiRef = createApiRef({
  id: "plugin.github.service"
});

const githubInsightsPlugin = createPlugin({
  id: "code-insights",
  apis: [
    createApiFactory({
      api: githubApiRef,
      deps: {
        githubAuthApi: githubAuthApiRef,
        errorApi: errorApiRef
      },
      factory: (deps) => new GithubClient(deps)
    })
  ],
  routes: {
    root: rootRouteRef
  }
});
const EntityGithubInsightsContent = githubInsightsPlugin.provide(
  createRoutableExtension({
    name: "EntityGithubInsightsContent",
    component: () => import('./esm/index-3c26abe8.esm.js').then((m) => m.InsightsPage),
    mountPoint: rootRouteRef
  })
);
const EntityGithubInsightsComplianceCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsComplianceCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.ComplianceCard)
    }
  })
);
const EntityGithubInsightsContributorsCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsContributorsCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.ContributorsCard)
    }
  })
);
const EntityGithubInsightsLanguagesCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsLanguagesCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.LanguagesCard)
    }
  })
);
const EntityGithubInsightsReadmeCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsReadmeCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.ReadMeCard)
    }
  })
);
const EntityGithubInsightsReleasesCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsReleasesCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.ReleasesCard)
    }
  })
);
const EntityGithubInsightsEnvironmentsCard = githubInsightsPlugin.provide(
  createComponentExtension({
    name: "EntityGithubInsightsEnvironmentsCard",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.EnvironmentsCard)
    }
  })
);
githubInsightsPlugin.provide(
  createComponentExtension({
    name: "GithubInsightsMarkdownContent",
    component: {
      lazy: () => import('./esm/index-a3ae55c6.esm.js').then((m) => m.MarkdownContent)
    }
  })
);

const GITHUB_INSIGHTS_ANNOTATION = "github.com/project-slug";
const isGithubInsightsAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[GITHUB_INSIGHTS_ANNOTATION]);
};

const defaultGithubIntegration = {
  hostname: "github.com",
  baseUrl: "https://api.github.com"
};
const useEntityGithubScmIntegration = (entity) => {
  var _a, _b;
  const integrations = useApi(scmIntegrationsApiRef);
  if (!entity) {
    return defaultGithubIntegration;
  }
  let location = (_a = entity.metadata.annotations) == null ? void 0 : _a[ANNOTATION_SOURCE_LOCATION];
  if (!location) {
    location = (_b = entity.metadata.annotations) == null ? void 0 : _b[ANNOTATION_LOCATION];
  }
  const { target } = parseLocationRef(location || "");
  const scm = integrations.github.byUrl(target);
  if (scm) {
    return {
      hostname: scm.config.host,
      baseUrl: scm.config.apiBaseUrl
    };
  }
  return defaultGithubIntegration;
};

class DefaultDict {
  constructor(defaultVal) {
    return new Proxy(
      {},
      {
        get: (target, name) => name in target ? target[name] : defaultVal
      }
    );
  }
}
const initialState = { etag: "", data: [] };
const createLicenseSlice = (set) => ({
  license: {
    state: initialState,
    setState: (next) => set(
      produce((draft) => {
        draft.license.state = next;
      })
    )
  }
});
const createBranchesSlice = (set) => ({
  branches: {
    state: initialState,
    setState: (next) => set(
      produce((draft) => {
        draft.branches.state = next;
      })
    )
  }
});
const createContributorSlice = (set) => ({
  contributor: {
    state: new DefaultDict(initialState),
    setState: (key, value) => set((prev) => {
      prev.contributor.state[key] = value;
      return produce((draft) => {
        draft.contributor.state = prev.contributor.state;
      });
    })
  }
});
const createRequestSlice = (set) => ({
  request: {
    state: new DefaultDict(initialState),
    setState: (key, value) => set((prev) => {
      prev.request.state[key] = value;
      return produce((draft) => {
        draft.requesst.state = prev.request.state;
      });
    })
  }
});
const useStore = create((set) => ({
  ...createLicenseSlice(set),
  ...createContributorSlice(set),
  ...createBranchesSlice(set),
  ...createRequestSlice(set)
}));

const useContributor = (username) => {
  const auth = useApi(githubAuthApiRef);
  const { entity } = useEntity();
  const { baseUrl } = useEntityGithubScmIntegration(entity);
  const { state: contributorData, setState: setContributorData } = useStore(
    (state) => state.contributor
  );
  const { value, loading, error } = useAsync(async () => {
    var _a;
    let result;
    try {
      const token = await auth.getAccessToken(["repo"]);
      const octokit = new Octokit({ auth: token });
      const response = await octokit.request(`GET /users/${username}`, {
        headers: { "if-none-match": contributorData[username].etag },
        baseUrl
      });
      result = {
        data: response.data,
        etag: (_a = response.headers.etag) != null ? _a : ""
      };
    } catch (e) {
      if (e.status === 304) {
        result = contributorData[username];
      }
    }
    return result;
  }, [username, baseUrl]);
  if (value) {
    setContributorData(username, value);
  }
  return {
    contributor: value ? value.data : void 0,
    loading,
    error
  };
};

const useStyles$3 = makeStyles((theme) => ({
  contributorsTooltipContainer: {
    padding: theme.spacing(1, 0)
  }
}));
const ContributorTooltipContent = ({ contributorLogin }) => {
  const classes = useStyles$3();
  const { entity } = useEntity();
  const { hostname } = useEntityGithubScmIntegration(entity);
  const { contributor, loading } = useContributor(contributorLogin);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (!(contributor == null ? void 0 : contributor.login)) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, "Fetching failed!");
  }
  return /* @__PURE__ */ React.createElement(Grid, { container: true, className: classes.contributorsTooltipContainer }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12, sm: 2 }, /* @__PURE__ */ React.createElement(
    Avatar,
    {
      key: contributor.login,
      alt: contributor.login,
      src: contributor.avatar_url
    }
  )), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12, sm: 10 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, /* @__PURE__ */ React.createElement(
    Link,
    {
      href: `//${hostname}/${contributor.login}`,
      color: "inherit",
      target: "_blank",
      rel: "noopener noreferrer"
    },
    contributor.name
  ), /* @__PURE__ */ React.createElement(Box, { component: "span", ml: 2 }, /* @__PURE__ */ React.createElement(Typography, { variant: "caption" }, contributor.login)))), contributor.bio && /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle2" }, contributor.bio)), contributor.location && /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Box, { my: 2 }, /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Typography, { variant: "caption" }, /* @__PURE__ */ React.createElement(LocationOnIcon, { fontSize: "inherit" }), " ", contributor.location))));
};

const LightTooltip = withStyles({
  tooltip: {
    backgroundColor: "white",
    border: "1px solid lightgrey",
    color: "#333",
    minWidth: "320px"
  }
})(Tooltip);
const Contributor = ({ contributor }) => /* @__PURE__ */ React.createElement(
  LightTooltip,
  {
    title: /* @__PURE__ */ React.createElement(ContributorTooltipContent, { contributorLogin: contributor.login }),
    interactive: true
  },
  /* @__PURE__ */ React.createElement(
    Avatar,
    {
      key: contributor.login,
      alt: contributor.login,
      src: contributor.avatar_url
    }
  )
);

const ContributorsList = ({ contributors }) => /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 1, justifyContent: "flex-start" }, contributors.map((contributor) => /* @__PURE__ */ React.createElement(Grid, { key: contributor.login, item: true }, /* @__PURE__ */ React.createElement(Contributor, { contributor }))));

const GITHUB_PROJECT_ANNOTATION = "github.com/project-slug";
const GITHUB_README_ANNOTATION = "github.com/project-readme-path";
const useProjectEntity = (entity) => {
  var _a, _b, _c, _d;
  const projectSlug = (_b = (_a = entity.metadata) == null ? void 0 : _a.annotations) == null ? void 0 : _b[GITHUB_PROJECT_ANNOTATION];
  const readmePath = (_d = (_c = entity.metadata) == null ? void 0 : _c.annotations) == null ? void 0 : _d[GITHUB_README_ANNOTATION];
  if (!projectSlug && projectSlug === "") {
    throw new Error(
      `No "${GITHUB_PROJECT_ANNOTATION}" annotation found for your entity ${entity.metadata.name}`
    );
  }
  return {
    owner: (projectSlug == null ? void 0 : projectSlug.split("/")[0]) || "",
    repo: (projectSlug == null ? void 0 : projectSlug.split("/")[1]) || "",
    readmePath
  };
};

const useRequest = (entity, requestName, perPage = 0, maxResults = 0) => {
  const auth = useApi(githubAuthApiRef);
  const { baseUrl } = useEntityGithubScmIntegration(entity);
  const { owner, repo } = useProjectEntity(entity);
  const { state: requestState, setState: setRequestState } = useStore(
    (state) => state.request
  );
  const { value, loading, error } = useAsync(async () => {
    var _a;
    let result;
    try {
      const token = await auth.getAccessToken(["repo"]);
      const octokit = new Octokit({ auth: token });
      const response = await octokit.request(
        `GET /repos/{owner}/{repo}/${requestName}`,
        {
          headers: { "if-none-match": requestState[requestName].etag },
          baseUrl,
          owner,
          repo,
          ...perPage && { per_page: perPage }
        }
      );
      const data = response.data;
      result = {
        data: maxResults ? data.slice(0, maxResults) : data,
        etag: (_a = response.headers.etag) != null ? _a : ""
      };
    } catch (e) {
      if (e.status === 304) {
        result = requestState[requestName];
      }
    }
    return result;
  }, [baseUrl, requestName]);
  if (value == null ? void 0 : value.data) {
    setRequestState(requestName, value);
  }
  return {
    value: value ? value.data : void 0,
    loading,
    error
  };
};

const useStyles$2 = makeStyles$1((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3),
    "& + .MuiAlert-root": {
      marginTop: theme.spacing(3)
    }
  }
}));
const ContributorsCard = () => {
  const { entity } = useEntity();
  const classes = useStyles$2();
  const { value, loading, error } = useRequest(entity, "contributors", 10);
  const { hostname } = useEntityGithubScmIntegration(entity);
  const projectAlert = isGithubInsightsAvailable(entity);
  const { owner, repo } = useProjectEntity(entity);
  if (!projectAlert) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error", className: classes.infoCard }, error.message);
  }
  return /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "Contributors",
      deepLink: {
        link: `//${hostname}/${owner}/${repo}/graphs/contributors`,
        title: "People",
        onClick: (e) => {
          e.preventDefault();
          window.open(`//${hostname}/${owner}/${repo}/graphs/contributors`);
        }
      },
      className: classes.infoCard
    },
    /* @__PURE__ */ React.createElement(ContributorsList, { contributors: value || [] })
  );
};
var ContributorsCard$1 = ContributorsCard;

const colors = {
  "1C Enterprise": {
    color: "#814CCC",
    url: "https://github.com/trending?l=1C-Enterprise"
  },
  "4D": {
    color: "inherit",
    url: "https://github.com/trending?l=4D"
  },
  ABAP: {
    color: "#E8274B",
    url: "https://github.com/trending?l=ABAP"
  },
  ActionScript: {
    color: "#882B0F",
    url: "https://github.com/trending?l=ActionScript"
  },
  Ada: {
    color: "#02f88c",
    url: "https://github.com/trending?l=Ada"
  },
  Agda: {
    color: "#315665",
    url: "https://github.com/trending?l=Agda"
  },
  "AGS Script": {
    color: "#B9D9FF",
    url: "https://github.com/trending?l=AGS-Script"
  },
  AL: {
    color: "#3AA2B5",
    url: "https://github.com/trending?l=AL"
  },
  Alloy: {
    color: "#64C800",
    url: "https://github.com/trending?l=Alloy"
  },
  "Alpine Abuild": {
    color: "inherit",
    url: "https://github.com/trending?l=Alpine-Abuild"
  },
  AMPL: {
    color: "#E6EFBB",
    url: "https://github.com/trending?l=AMPL"
  },
  AngelScript: {
    color: "#C7D7DC",
    url: "https://github.com/trending?l=AngelScript"
  },
  ANTLR: {
    color: "#9DC3FF",
    url: "https://github.com/trending?l=ANTLR"
  },
  Apex: {
    color: "#1797c0",
    url: "https://github.com/trending?l=Apex"
  },
  "API Blueprint": {
    color: "#2ACCA8",
    url: "https://github.com/trending?l=API-Blueprint"
  },
  APL: {
    color: "#5A8164",
    url: "https://github.com/trending?l=APL"
  },
  "Apollo Guidance Computer": {
    color: "#0B3D91",
    url: "https://github.com/trending?l=Apollo-Guidance-Computer"
  },
  AppleScript: {
    color: "#101F1F",
    url: "https://github.com/trending?l=AppleScript"
  },
  Arc: {
    color: "#aa2afe",
    url: "https://github.com/trending?l=Arc"
  },
  ASL: {
    color: "inherit",
    url: "https://github.com/trending?l=ASL"
  },
  "ASP.NET": {
    color: "#9400ff",
    url: "https://github.com/trending?l=ASP.NET"
  },
  AspectJ: {
    color: "#a957b0",
    url: "https://github.com/trending?l=AspectJ"
  },
  Assembly: {
    color: "#6E4C13",
    url: "https://github.com/trending?l=Assembly"
  },
  Asymptote: {
    color: "#ff0000",
    url: "https://github.com/trending?l=Asymptote"
  },
  ATS: {
    color: "#1ac620",
    url: "https://github.com/trending?l=ATS"
  },
  Augeas: {
    color: "inherit",
    url: "https://github.com/trending?l=Augeas"
  },
  AutoHotkey: {
    color: "#6594b9",
    url: "https://github.com/trending?l=AutoHotkey"
  },
  AutoIt: {
    color: "#1C3552",
    url: "https://github.com/trending?l=AutoIt"
  },
  Awk: {
    color: "inherit",
    url: "https://github.com/trending?l=Awk"
  },
  Ballerina: {
    color: "#FF5000",
    url: "https://github.com/trending?l=Ballerina"
  },
  Batchfile: {
    color: "#C1F12E",
    url: "https://github.com/trending?l=Batchfile"
  },
  Befunge: {
    color: "inherit",
    url: "https://github.com/trending?l=Befunge"
  },
  Bison: {
    color: "#6A463F",
    url: "https://github.com/trending?l=Bison"
  },
  BitBake: {
    color: "inherit",
    url: "https://github.com/trending?l=BitBake"
  },
  Blade: {
    color: "#f7523f",
    url: "https://github.com/trending?l=Blade"
  },
  BlitzBasic: {
    color: "inherit",
    url: "https://github.com/trending?l=BlitzBasic"
  },
  BlitzMax: {
    color: "#cd6400",
    url: "https://github.com/trending?l=BlitzMax"
  },
  Bluespec: {
    color: "inherit",
    url: "https://github.com/trending?l=Bluespec"
  },
  Boo: {
    color: "#d4bec1",
    url: "https://github.com/trending?l=Boo"
  },
  Brainfuck: {
    color: "#2F2530",
    url: "https://github.com/trending?l=Brainfuck"
  },
  Brightscript: {
    color: "inherit",
    url: "https://github.com/trending?l=Brightscript"
  },
  C: {
    color: "#555555",
    url: "https://github.com/trending?l=C"
  },
  "C#": {
    color: "#178600",
    url: "https://github.com/trending?l=Csharp"
  },
  "C++": {
    color: "#f34b7d",
    url: "https://github.com/trending?l=C++"
  },
  "C2hs Haskell": {
    color: "inherit",
    url: "https://github.com/trending?l=C2hs-Haskell"
  },
  "Cap'n Proto": {
    color: "inherit",
    url: "https://github.com/trending?l=Cap'n-Proto"
  },
  CartoCSS: {
    color: "inherit",
    url: "https://github.com/trending?l=CartoCSS"
  },
  Ceylon: {
    color: "#dfa535",
    url: "https://github.com/trending?l=Ceylon"
  },
  Chapel: {
    color: "#8dc63f",
    url: "https://github.com/trending?l=Chapel"
  },
  Charity: {
    color: "inherit",
    url: "https://github.com/trending?l=Charity"
  },
  ChucK: {
    color: "inherit",
    url: "https://github.com/trending?l=ChucK"
  },
  Cirru: {
    color: "#ccccff",
    url: "https://github.com/trending?l=Cirru"
  },
  Clarion: {
    color: "#db901e",
    url: "https://github.com/trending?l=Clarion"
  },
  "Classic ASP": {
    color: "#6a40fd",
    url: "https://github.com/trending?l=Classic-ASP"
  },
  Clean: {
    color: "#3F85AF",
    url: "https://github.com/trending?l=Clean"
  },
  Click: {
    color: "#E4E6F3",
    url: "https://github.com/trending?l=Click"
  },
  CLIPS: {
    color: "inherit",
    url: "https://github.com/trending?l=CLIPS"
  },
  Clojure: {
    color: "#db5855",
    url: "https://github.com/trending?l=Clojure"
  },
  CMake: {
    color: "inherit",
    url: "https://github.com/trending?l=CMake"
  },
  COBOL: {
    color: "inherit",
    url: "https://github.com/trending?l=COBOL"
  },
  CodeQL: {
    color: "inherit",
    url: "https://github.com/trending?l=CodeQL"
  },
  CoffeeScript: {
    color: "#244776",
    url: "https://github.com/trending?l=CoffeeScript"
  },
  ColdFusion: {
    color: "#ed2cd6",
    url: "https://github.com/trending?l=ColdFusion"
  },
  "ColdFusion CFC": {
    color: "#ed2cd6",
    url: "https://github.com/trending?l=ColdFusion-CFC"
  },
  "Common Lisp": {
    color: "#3fb68b",
    url: "https://github.com/trending?l=Common-Lisp"
  },
  "Common Workflow Language": {
    color: "#B5314C",
    url: "https://github.com/trending?l=Common-Workflow-Language"
  },
  "Component Pascal": {
    color: "#B0CE4E",
    url: "https://github.com/trending?l=Component-Pascal"
  },
  Cool: {
    color: "inherit",
    url: "https://github.com/trending?l=Cool"
  },
  Coq: {
    color: "inherit",
    url: "https://github.com/trending?l=Coq"
  },
  Crystal: {
    color: "#000100",
    url: "https://github.com/trending?l=Crystal"
  },
  CSON: {
    color: "#244776",
    url: "https://github.com/trending?l=CSON"
  },
  Csound: {
    color: "inherit",
    url: "https://github.com/trending?l=Csound"
  },
  "Csound Document": {
    color: "inherit",
    url: "https://github.com/trending?l=Csound-Document"
  },
  "Csound Score": {
    color: "inherit",
    url: "https://github.com/trending?l=Csound-Score"
  },
  CSS: {
    color: "#563d7c",
    url: "https://github.com/trending?l=CSS"
  },
  Cuda: {
    color: "#3A4E3A",
    url: "https://github.com/trending?l=Cuda"
  },
  CWeb: {
    color: "inherit",
    url: "https://github.com/trending?l=CWeb"
  },
  Cycript: {
    color: "inherit",
    url: "https://github.com/trending?l=Cycript"
  },
  Cython: {
    color: "inherit",
    url: "https://github.com/trending?l=Cython"
  },
  D: {
    color: "#ba595e",
    url: "https://github.com/trending?l=D"
  },
  Dafny: {
    color: "#FFEC25",
    url: "https://github.com/trending?l=Dafny"
  },
  Dart: {
    color: "#00B4AB",
    url: "https://github.com/trending?l=Dart"
  },
  DataWeave: {
    color: "#003a52",
    url: "https://github.com/trending?l=DataWeave"
  },
  Dhall: {
    color: "#dfafff",
    url: "https://github.com/trending?l=Dhall"
  },
  "DIGITAL Command Language": {
    color: "inherit",
    url: "https://github.com/trending?l=DIGITAL-Command-Language"
  },
  DM: {
    color: "#447265",
    url: "https://github.com/trending?l=DM"
  },
  Dockerfile: {
    color: "#384d54",
    url: "https://github.com/trending?l=Dockerfile"
  },
  Dogescript: {
    color: "#cca760",
    url: "https://github.com/trending?l=Dogescript"
  },
  DTrace: {
    color: "inherit",
    url: "https://github.com/trending?l=DTrace"
  },
  Dylan: {
    color: "#6c616e",
    url: "https://github.com/trending?l=Dylan"
  },
  E: {
    color: "#ccce35",
    url: "https://github.com/trending?l=E"
  },
  eC: {
    color: "#913960",
    url: "https://github.com/trending?l=eC"
  },
  ECL: {
    color: "#8a1267",
    url: "https://github.com/trending?l=ECL"
  },
  ECLiPSe: {
    color: "inherit",
    url: "https://github.com/trending?l=ECLiPSe"
  },
  Eiffel: {
    color: "#4d6977",
    url: "https://github.com/trending?l=Eiffel"
  },
  EJS: {
    color: "#a91e50",
    url: "https://github.com/trending?l=EJS"
  },
  Elixir: {
    color: "#6e4a7e",
    url: "https://github.com/trending?l=Elixir"
  },
  Elm: {
    color: "#60B5CC",
    url: "https://github.com/trending?l=Elm"
  },
  "Emacs Lisp": {
    color: "#c065db",
    url: "https://github.com/trending?l=Emacs-Lisp"
  },
  EmberScript: {
    color: "#FFF4F3",
    url: "https://github.com/trending?l=EmberScript"
  },
  EQ: {
    color: "#a78649",
    url: "https://github.com/trending?l=EQ"
  },
  Erlang: {
    color: "#B83998",
    url: "https://github.com/trending?l=Erlang"
  },
  "F#": {
    color: "#b845fc",
    url: "https://github.com/trending?l=Fsharp"
  },
  "F*": {
    color: "#572e30",
    url: "https://github.com/trending?l=F*"
  },
  Factor: {
    color: "#636746",
    url: "https://github.com/trending?l=Factor"
  },
  Fancy: {
    color: "#7b9db4",
    url: "https://github.com/trending?l=Fancy"
  },
  Fantom: {
    color: "#14253c",
    url: "https://github.com/trending?l=Fantom"
  },
  Faust: {
    color: "#c37240",
    url: "https://github.com/trending?l=Faust"
  },
  "Filebench WML": {
    color: "inherit",
    url: "https://github.com/trending?l=Filebench-WML"
  },
  Filterscript: {
    color: "inherit",
    url: "https://github.com/trending?l=Filterscript"
  },
  fish: {
    color: "inherit",
    url: "https://github.com/trending?l=fish"
  },
  FLUX: {
    color: "#88ccff",
    url: "https://github.com/trending?l=FLUX"
  },
  Forth: {
    color: "#341708",
    url: "https://github.com/trending?l=Forth"
  },
  Fortran: {
    color: "#4d41b1",
    url: "https://github.com/trending?l=Fortran"
  },
  "Fortran Free Form": {
    color: "inherit",
    url: "https://github.com/trending?l=Fortran-Free-Form"
  },
  FreeMarker: {
    color: "#0050b2",
    url: "https://github.com/trending?l=FreeMarker"
  },
  Frege: {
    color: "#00cafe",
    url: "https://github.com/trending?l=Frege"
  },
  Futhark: {
    color: "#5f021f",
    url: "https://github.com/trending?l=Futhark"
  },
  "G-code": {
    color: "#D08CF2",
    url: "https://github.com/trending?l=G-code"
  },
  "Game Maker Language": {
    color: "#71b417",
    url: "https://github.com/trending?l=Game-Maker-Language"
  },
  GAML: {
    color: "#FFC766",
    url: "https://github.com/trending?l=GAML"
  },
  GAMS: {
    color: "inherit",
    url: "https://github.com/trending?l=GAMS"
  },
  GAP: {
    color: "inherit",
    url: "https://github.com/trending?l=GAP"
  },
  "GCC Machine Description": {
    color: "inherit",
    url: "https://github.com/trending?l=GCC-Machine-Description"
  },
  GDB: {
    color: "inherit",
    url: "https://github.com/trending?l=GDB"
  },
  GDScript: {
    color: "#355570",
    url: "https://github.com/trending?l=GDScript"
  },
  Genie: {
    color: "#fb855d",
    url: "https://github.com/trending?l=Genie"
  },
  Genshi: {
    color: "inherit",
    url: "https://github.com/trending?l=Genshi"
  },
  "Gentoo Ebuild": {
    color: "inherit",
    url: "https://github.com/trending?l=Gentoo-Ebuild"
  },
  "Gentoo Eclass": {
    color: "inherit",
    url: "https://github.com/trending?l=Gentoo-Eclass"
  },
  Gherkin: {
    color: "#5B2063",
    url: "https://github.com/trending?l=Gherkin"
  },
  GLSL: {
    color: "inherit",
    url: "https://github.com/trending?l=GLSL"
  },
  Glyph: {
    color: "#c1ac7f",
    url: "https://github.com/trending?l=Glyph"
  },
  Gnuplot: {
    color: "#f0a9f0",
    url: "https://github.com/trending?l=Gnuplot"
  },
  Go: {
    color: "#00ADD8",
    url: "https://github.com/trending?l=Go"
  },
  Golo: {
    color: "#88562A",
    url: "https://github.com/trending?l=Golo"
  },
  Gosu: {
    color: "#82937f",
    url: "https://github.com/trending?l=Gosu"
  },
  Grace: {
    color: "inherit",
    url: "https://github.com/trending?l=Grace"
  },
  "Grammatical Framework": {
    color: "#ff0000",
    url: "https://github.com/trending?l=Grammatical-Framework"
  },
  GraphQL: {
    color: "#e10098",
    url: "https://github.com/trending?l=GraphQL"
  },
  Groovy: {
    color: "#e69f56",
    url: "https://github.com/trending?l=Groovy"
  },
  "Groovy Server Pages": {
    color: "inherit",
    url: "https://github.com/trending?l=Groovy-Server-Pages"
  },
  Hack: {
    color: "#878787",
    url: "https://github.com/trending?l=Hack"
  },
  Haml: {
    color: "#ece2a9",
    url: "https://github.com/trending?l=Haml"
  },
  Handlebars: {
    color: "#f7931e",
    url: "https://github.com/trending?l=Handlebars"
  },
  Harbour: {
    color: "#0e60e3",
    url: "https://github.com/trending?l=Harbour"
  },
  Haskell: {
    color: "#5e5086",
    url: "https://github.com/trending?l=Haskell"
  },
  Haxe: {
    color: "#df7900",
    url: "https://github.com/trending?l=Haxe"
  },
  HCL: {
    color: "#00dfd4",
    url: "https://github.com/trending?l=HCL"
  },
  HiveQL: {
    color: "#dce200",
    url: "https://github.com/trending?l=HiveQL"
  },
  HLSL: {
    color: "inherit",
    url: "https://github.com/trending?l=HLSL"
  },
  HolyC: {
    color: "#ffefaf",
    url: "https://github.com/trending?l=HolyC"
  },
  HTML: {
    color: "#e34c26",
    url: "https://github.com/trending?l=HTML"
  },
  Hy: {
    color: "#7790B2",
    url: "https://github.com/trending?l=Hy"
  },
  HyPhy: {
    color: "inherit",
    url: "https://github.com/trending?l=HyPhy"
  },
  IDL: {
    color: "#a3522f",
    url: "https://github.com/trending?l=IDL"
  },
  Idris: {
    color: "#b30000",
    url: "https://github.com/trending?l=Idris"
  },
  "IGOR Pro": {
    color: "#0000cc",
    url: "https://github.com/trending?l=IGOR-Pro"
  },
  "Inform 7": {
    color: "inherit",
    url: "https://github.com/trending?l=Inform-7"
  },
  "Inno Setup": {
    color: "inherit",
    url: "https://github.com/trending?l=Inno-Setup"
  },
  Io: {
    color: "#a9188d",
    url: "https://github.com/trending?l=Io"
  },
  Ioke: {
    color: "#078193",
    url: "https://github.com/trending?l=Ioke"
  },
  Isabelle: {
    color: "#FEFE00",
    url: "https://github.com/trending?l=Isabelle"
  },
  "Isabelle ROOT": {
    color: "inherit",
    url: "https://github.com/trending?l=Isabelle-ROOT"
  },
  J: {
    color: "#9EEDFF",
    url: "https://github.com/trending?l=J"
  },
  Jasmin: {
    color: "inherit",
    url: "https://github.com/trending?l=Jasmin"
  },
  Java: {
    color: "#b07219",
    url: "https://github.com/trending?l=Java"
  },
  "Java Server Pages": {
    color: "inherit",
    url: "https://github.com/trending?l=Java-Server-Pages"
  },
  JavaScript: {
    color: "#f1e05a",
    url: "https://github.com/trending?l=JavaScript"
  },
  "JavaScript+ERB": {
    color: "inherit",
    url: "https://github.com/trending?l=JavaScript+ERB"
  },
  JFlex: {
    color: "#DBCA00",
    url: "https://github.com/trending?l=JFlex"
  },
  Jison: {
    color: "inherit",
    url: "https://github.com/trending?l=Jison"
  },
  "Jison Lex": {
    color: "inherit",
    url: "https://github.com/trending?l=Jison-Lex"
  },
  Jolie: {
    color: "#843179",
    url: "https://github.com/trending?l=Jolie"
  },
  JSONiq: {
    color: "#40d47e",
    url: "https://github.com/trending?l=JSONiq"
  },
  Jsonnet: {
    color: "#0064bd",
    url: "https://github.com/trending?l=Jsonnet"
  },
  JSX: {
    color: "inherit",
    url: "https://github.com/trending?l=JSX"
  },
  Julia: {
    color: "#a270ba",
    url: "https://github.com/trending?l=Julia"
  },
  "Jupyter Notebook": {
    color: "#DA5B0B",
    url: "https://github.com/trending?l=Jupyter-Notebook"
  },
  "Kaitai Struct": {
    color: "#773b37",
    url: "https://github.com/trending?l=Kaitai-Struct"
  },
  Kotlin: {
    color: "#F18E33",
    url: "https://github.com/trending?l=Kotlin"
  },
  KRL: {
    color: "#28430A",
    url: "https://github.com/trending?l=KRL"
  },
  LabVIEW: {
    color: "inherit",
    url: "https://github.com/trending?l=LabVIEW"
  },
  Lasso: {
    color: "#999999",
    url: "https://github.com/trending?l=Lasso"
  },
  Latte: {
    color: "#f2a542",
    url: "https://github.com/trending?l=Latte"
  },
  Lean: {
    color: "inherit",
    url: "https://github.com/trending?l=Lean"
  },
  Less: {
    color: "#1d365d",
    url: "https://github.com/trending?l=Less"
  },
  Lex: {
    color: "#DBCA00",
    url: "https://github.com/trending?l=Lex"
  },
  LFE: {
    color: "#4C3023",
    url: "https://github.com/trending?l=LFE"
  },
  LilyPond: {
    color: "inherit",
    url: "https://github.com/trending?l=LilyPond"
  },
  Limbo: {
    color: "inherit",
    url: "https://github.com/trending?l=Limbo"
  },
  "Literate Agda": {
    color: "inherit",
    url: "https://github.com/trending?l=Literate-Agda"
  },
  "Literate CoffeeScript": {
    color: "inherit",
    url: "https://github.com/trending?l=Literate-CoffeeScript"
  },
  "Literate Haskell": {
    color: "inherit",
    url: "https://github.com/trending?l=Literate-Haskell"
  },
  LiveScript: {
    color: "#499886",
    url: "https://github.com/trending?l=LiveScript"
  },
  LLVM: {
    color: "#185619",
    url: "https://github.com/trending?l=LLVM"
  },
  Logos: {
    color: "inherit",
    url: "https://github.com/trending?l=Logos"
  },
  Logtalk: {
    color: "inherit",
    url: "https://github.com/trending?l=Logtalk"
  },
  LOLCODE: {
    color: "#cc9900",
    url: "https://github.com/trending?l=LOLCODE"
  },
  LookML: {
    color: "#652B81",
    url: "https://github.com/trending?l=LookML"
  },
  LoomScript: {
    color: "inherit",
    url: "https://github.com/trending?l=LoomScript"
  },
  LSL: {
    color: "#3d9970",
    url: "https://github.com/trending?l=LSL"
  },
  Lua: {
    color: "#000080",
    url: "https://github.com/trending?l=Lua"
  },
  M: {
    color: "inherit",
    url: "https://github.com/trending?l=M"
  },
  M4: {
    color: "inherit",
    url: "https://github.com/trending?l=M4"
  },
  M4Sugar: {
    color: "inherit",
    url: "https://github.com/trending?l=M4Sugar"
  },
  Macaulay2: {
    color: "#d8ffff",
    url: "https://github.com/trending?l=Macaulay2"
  },
  Makefile: {
    color: "#427819",
    url: "https://github.com/trending?l=Makefile"
  },
  Mako: {
    color: "inherit",
    url: "https://github.com/trending?l=Mako"
  },
  Markdown: {
    color: "#083fa1",
    url: "https://github.com/trending?l=Markdown"
  },
  Marko: {
    color: "#42bff2",
    url: "https://github.com/trending?l=Marko"
  },
  Mask: {
    color: "#f97732",
    url: "https://github.com/trending?l=Mask"
  },
  Mathematica: {
    color: "inherit",
    url: "https://github.com/trending?l=Mathematica"
  },
  MATLAB: {
    color: "#e16737",
    url: "https://github.com/trending?l=MATLAB"
  },
  Max: {
    color: "#c4a79c",
    url: "https://github.com/trending?l=Max"
  },
  MAXScript: {
    color: "#00a6a6",
    url: "https://github.com/trending?l=MAXScript"
  },
  mcfunction: {
    color: "#E22837",
    url: "https://github.com/trending?l=mcfunction"
  },
  Mercury: {
    color: "#ff2b2b",
    url: "https://github.com/trending?l=Mercury"
  },
  Meson: {
    color: "#007800",
    url: "https://github.com/trending?l=Meson"
  },
  Metal: {
    color: "#8f14e9",
    url: "https://github.com/trending?l=Metal"
  },
  MiniD: {
    color: "inherit",
    url: "https://github.com/trending?l=MiniD"
  },
  Mirah: {
    color: "#c7a938",
    url: "https://github.com/trending?l=Mirah"
  },
  "mIRC Script": {
    color: "#3d57c3",
    url: "https://github.com/trending?l=mIRC-Script"
  },
  MLIR: {
    color: "#5EC8DB",
    url: "https://github.com/trending?l=MLIR"
  },
  Modelica: {
    color: "inherit",
    url: "https://github.com/trending?l=Modelica"
  },
  "Modula-2": {
    color: "inherit",
    url: "https://github.com/trending?l=Modula-2"
  },
  "Modula-3": {
    color: "#223388",
    url: "https://github.com/trending?l=Modula-3"
  },
  "Module Management System": {
    color: "inherit",
    url: "https://github.com/trending?l=Module-Management-System"
  },
  Monkey: {
    color: "inherit",
    url: "https://github.com/trending?l=Monkey"
  },
  Moocode: {
    color: "inherit",
    url: "https://github.com/trending?l=Moocode"
  },
  MoonScript: {
    color: "inherit",
    url: "https://github.com/trending?l=MoonScript"
  },
  "Motorola 68K Assembly": {
    color: "inherit",
    url: "https://github.com/trending?l=Motorola-68K-Assembly"
  },
  MQL4: {
    color: "#62A8D6",
    url: "https://github.com/trending?l=MQL4"
  },
  MQL5: {
    color: "#4A76B8",
    url: "https://github.com/trending?l=MQL5"
  },
  MTML: {
    color: "#b7e1f4",
    url: "https://github.com/trending?l=MTML"
  },
  MUF: {
    color: "inherit",
    url: "https://github.com/trending?l=MUF"
  },
  mupad: {
    color: "inherit",
    url: "https://github.com/trending?l=mupad"
  },
  Myghty: {
    color: "inherit",
    url: "https://github.com/trending?l=Myghty"
  },
  NASL: {
    color: "inherit",
    url: "https://github.com/trending?l=NASL"
  },
  NCL: {
    color: "#28431f",
    url: "https://github.com/trending?l=NCL"
  },
  Nearley: {
    color: "#990000",
    url: "https://github.com/trending?l=Nearley"
  },
  Nemerle: {
    color: "#3d3c6e",
    url: "https://github.com/trending?l=Nemerle"
  },
  nesC: {
    color: "#94B0C7",
    url: "https://github.com/trending?l=nesC"
  },
  NetLinx: {
    color: "#0aa0ff",
    url: "https://github.com/trending?l=NetLinx"
  },
  "NetLinx+ERB": {
    color: "#747faa",
    url: "https://github.com/trending?l=NetLinx+ERB"
  },
  NetLogo: {
    color: "#ff6375",
    url: "https://github.com/trending?l=NetLogo"
  },
  NewLisp: {
    color: "#87AED7",
    url: "https://github.com/trending?l=NewLisp"
  },
  Nextflow: {
    color: "#3ac486",
    url: "https://github.com/trending?l=Nextflow"
  },
  Nim: {
    color: "#ffc200",
    url: "https://github.com/trending?l=Nim"
  },
  Nit: {
    color: "#009917",
    url: "https://github.com/trending?l=Nit"
  },
  Nix: {
    color: "#7e7eff",
    url: "https://github.com/trending?l=Nix"
  },
  NSIS: {
    color: "inherit",
    url: "https://github.com/trending?l=NSIS"
  },
  Nu: {
    color: "#c9df40",
    url: "https://github.com/trending?l=Nu"
  },
  NumPy: {
    color: "#9C8AF9",
    url: "https://github.com/trending?l=NumPy"
  },
  "Objective-C": {
    color: "#438eff",
    url: "https://github.com/trending?l=Objective-C"
  },
  "Objective-C++": {
    color: "#6866fb",
    url: "https://github.com/trending?l=Objective-C++"
  },
  "Objective-J": {
    color: "#ff0c5a",
    url: "https://github.com/trending?l=Objective-J"
  },
  ObjectScript: {
    color: "#424893",
    url: "https://github.com/trending?l=ObjectScript"
  },
  OCaml: {
    color: "#3be133",
    url: "https://github.com/trending?l=OCaml"
  },
  Odin: {
    color: "#60AFFE",
    url: "https://github.com/trending?l=Odin"
  },
  Omgrofl: {
    color: "#cabbff",
    url: "https://github.com/trending?l=Omgrofl"
  },
  ooc: {
    color: "#b0b77e",
    url: "https://github.com/trending?l=ooc"
  },
  Opa: {
    color: "inherit",
    url: "https://github.com/trending?l=Opa"
  },
  Opal: {
    color: "#f7ede0",
    url: "https://github.com/trending?l=Opal"
  },
  "Open Policy Agent": {
    color: "inherit",
    url: "https://github.com/trending?l=Open-Policy-Agent"
  },
  OpenCL: {
    color: "inherit",
    url: "https://github.com/trending?l=OpenCL"
  },
  "OpenEdge ABL": {
    color: "inherit",
    url: "https://github.com/trending?l=OpenEdge-ABL"
  },
  OpenQASM: {
    color: "#AA70FF",
    url: "https://github.com/trending?l=OpenQASM"
  },
  "OpenRC runscript": {
    color: "inherit",
    url: "https://github.com/trending?l=OpenRC-runscript"
  },
  OpenSCAD: {
    color: "inherit",
    url: "https://github.com/trending?l=OpenSCAD"
  },
  Ox: {
    color: "inherit",
    url: "https://github.com/trending?l=Ox"
  },
  Oxygene: {
    color: "#cdd0e3",
    url: "https://github.com/trending?l=Oxygene"
  },
  Oz: {
    color: "#fab738",
    url: "https://github.com/trending?l=Oz"
  },
  P4: {
    color: "#7055b5",
    url: "https://github.com/trending?l=P4"
  },
  Pan: {
    color: "#cc0000",
    url: "https://github.com/trending?l=Pan"
  },
  Papyrus: {
    color: "#6600cc",
    url: "https://github.com/trending?l=Papyrus"
  },
  Parrot: {
    color: "#f3ca0a",
    url: "https://github.com/trending?l=Parrot"
  },
  "Parrot Assembly": {
    color: "inherit",
    url: "https://github.com/trending?l=Parrot-Assembly"
  },
  "Parrot Internal Representation": {
    color: "inherit",
    url: "https://github.com/trending?l=Parrot-Internal-Representation"
  },
  Pascal: {
    color: "#E3F171",
    url: "https://github.com/trending?l=Pascal"
  },
  Pawn: {
    color: "#dbb284",
    url: "https://github.com/trending?l=Pawn"
  },
  Pep8: {
    color: "#C76F5B",
    url: "https://github.com/trending?l=Pep8"
  },
  Perl: {
    color: "#0298c3",
    url: "https://github.com/trending?l=Perl"
  },
  PHP: {
    color: "#4F5D95",
    url: "https://github.com/trending?l=PHP"
  },
  PicoLisp: {
    color: "inherit",
    url: "https://github.com/trending?l=PicoLisp"
  },
  PigLatin: {
    color: "#fcd7de",
    url: "https://github.com/trending?l=PigLatin"
  },
  Pike: {
    color: "#005390",
    url: "https://github.com/trending?l=Pike"
  },
  PLpgSQL: {
    color: "inherit",
    url: "https://github.com/trending?l=PLpgSQL"
  },
  PLSQL: {
    color: "#dad8d8",
    url: "https://github.com/trending?l=PLSQL"
  },
  PogoScript: {
    color: "#d80074",
    url: "https://github.com/trending?l=PogoScript"
  },
  Pony: {
    color: "inherit",
    url: "https://github.com/trending?l=Pony"
  },
  PostScript: {
    color: "#da291c",
    url: "https://github.com/trending?l=PostScript"
  },
  "POV-Ray SDL": {
    color: "inherit",
    url: "https://github.com/trending?l=POV-Ray-SDL"
  },
  PowerBuilder: {
    color: "#8f0f8d",
    url: "https://github.com/trending?l=PowerBuilder"
  },
  PowerShell: {
    color: "#012456",
    url: "https://github.com/trending?l=PowerShell"
  },
  Prisma: {
    color: "#0c344b",
    url: "https://github.com/trending?l=Prisma"
  },
  Processing: {
    color: "#0096D8",
    url: "https://github.com/trending?l=Processing"
  },
  Prolog: {
    color: "#74283c",
    url: "https://github.com/trending?l=Prolog"
  },
  "Propeller Spin": {
    color: "#7fa2a7",
    url: "https://github.com/trending?l=Propeller-Spin"
  },
  Pug: {
    color: "#a86454",
    url: "https://github.com/trending?l=Pug"
  },
  Puppet: {
    color: "#302B6D",
    url: "https://github.com/trending?l=Puppet"
  },
  PureBasic: {
    color: "#5a6986",
    url: "https://github.com/trending?l=PureBasic"
  },
  PureScript: {
    color: "#1D222D",
    url: "https://github.com/trending?l=PureScript"
  },
  Python: {
    color: "#3572A5",
    url: "https://github.com/trending?l=Python"
  },
  "Python console": {
    color: "inherit",
    url: "https://github.com/trending?l=Python-console"
  },
  q: {
    color: "#0040cd",
    url: "https://github.com/trending?l=q"
  },
  "Q#": {
    color: "#fed659",
    url: "https://github.com/trending?l=Qsharp"
  },
  QMake: {
    color: "inherit",
    url: "https://github.com/trending?l=QMake"
  },
  QML: {
    color: "#44a51c",
    url: "https://github.com/trending?l=QML"
  },
  "Qt Script": {
    color: "#00b841",
    url: "https://github.com/trending?l=Qt-Script"
  },
  Quake: {
    color: "#882233",
    url: "https://github.com/trending?l=Quake"
  },
  R: {
    color: "#198CE7",
    url: "https://github.com/trending?l=R"
  },
  Racket: {
    color: "#3c5caa",
    url: "https://github.com/trending?l=Racket"
  },
  Ragel: {
    color: "#9d5200",
    url: "https://github.com/trending?l=Ragel"
  },
  Raku: {
    color: "#0000fb",
    url: "https://github.com/trending?l=Raku"
  },
  RAML: {
    color: "#77d9fb",
    url: "https://github.com/trending?l=RAML"
  },
  Rascal: {
    color: "#fffaa0",
    url: "https://github.com/trending?l=Rascal"
  },
  REALbasic: {
    color: "inherit",
    url: "https://github.com/trending?l=REALbasic"
  },
  Reason: {
    color: "#ff5847",
    url: "https://github.com/trending?l=Reason"
  },
  Rebol: {
    color: "#358a5b",
    url: "https://github.com/trending?l=Rebol"
  },
  Red: {
    color: "#f50000",
    url: "https://github.com/trending?l=Red"
  },
  Redcode: {
    color: "inherit",
    url: "https://github.com/trending?l=Redcode"
  },
  "Ren'Py": {
    color: "#ff7f7f",
    url: "https://github.com/trending?l=Ren'Py"
  },
  RenderScript: {
    color: "inherit",
    url: "https://github.com/trending?l=RenderScript"
  },
  REXX: {
    color: "inherit",
    url: "https://github.com/trending?l=REXX"
  },
  Ring: {
    color: "#2D54CB",
    url: "https://github.com/trending?l=Ring"
  },
  Riot: {
    color: "#A71E49",
    url: "https://github.com/trending?l=Riot"
  },
  RobotFramework: {
    color: "inherit",
    url: "https://github.com/trending?l=RobotFramework"
  },
  Roff: {
    color: "#ecdebe",
    url: "https://github.com/trending?l=Roff"
  },
  Rouge: {
    color: "#cc0088",
    url: "https://github.com/trending?l=Rouge"
  },
  RPC: {
    color: "inherit",
    url: "https://github.com/trending?l=RPC"
  },
  Ruby: {
    color: "#701516",
    url: "https://github.com/trending?l=Ruby"
  },
  RUNOFF: {
    color: "#665a4e",
    url: "https://github.com/trending?l=RUNOFF"
  },
  Rust: {
    color: "#dea584",
    url: "https://github.com/trending?l=Rust"
  },
  Sage: {
    color: "inherit",
    url: "https://github.com/trending?l=Sage"
  },
  SaltStack: {
    color: "#646464",
    url: "https://github.com/trending?l=SaltStack"
  },
  SAS: {
    color: "#B34936",
    url: "https://github.com/trending?l=SAS"
  },
  Sass: {
    color: "#a53b70",
    url: "https://github.com/trending?l=Sass"
  },
  Scala: {
    color: "#c22d40",
    url: "https://github.com/trending?l=Scala"
  },
  Scheme: {
    color: "#1e4aec",
    url: "https://github.com/trending?l=Scheme"
  },
  Scilab: {
    color: "inherit",
    url: "https://github.com/trending?l=Scilab"
  },
  SCSS: {
    color: "#c6538c",
    url: "https://github.com/trending?l=SCSS"
  },
  sed: {
    color: "#64b970",
    url: "https://github.com/trending?l=sed"
  },
  Self: {
    color: "#0579aa",
    url: "https://github.com/trending?l=Self"
  },
  ShaderLab: {
    color: "inherit",
    url: "https://github.com/trending?l=ShaderLab"
  },
  Shell: {
    color: "#89e051",
    url: "https://github.com/trending?l=Shell"
  },
  ShellSession: {
    color: "inherit",
    url: "https://github.com/trending?l=ShellSession"
  },
  Shen: {
    color: "#120F14",
    url: "https://github.com/trending?l=Shen"
  },
  Sieve: {
    color: "inherit",
    url: "https://github.com/trending?l=Sieve"
  },
  Slash: {
    color: "#007eff",
    url: "https://github.com/trending?l=Slash"
  },
  Slice: {
    color: "#003fa2",
    url: "https://github.com/trending?l=Slice"
  },
  Slim: {
    color: "#2b2b2b",
    url: "https://github.com/trending?l=Slim"
  },
  Smali: {
    color: "inherit",
    url: "https://github.com/trending?l=Smali"
  },
  Smalltalk: {
    color: "#596706",
    url: "https://github.com/trending?l=Smalltalk"
  },
  Smarty: {
    color: "#bedf00",
    url: "https://github.com/trending?l=Smarty"
  },
  SmPL: {
    color: "#c94949",
    url: "https://github.com/trending?l=SmPL"
  },
  SMT: {
    color: "inherit",
    url: "https://github.com/trending?l=SMT"
  },
  Solidity: {
    color: "#AA6746",
    url: "https://github.com/trending?l=Solidity"
  },
  SourcePawn: {
    color: "#f69e1d",
    url: "https://github.com/trending?l=SourcePawn"
  },
  SQF: {
    color: "#3F3F3F",
    url: "https://github.com/trending?l=SQF"
  },
  SQLPL: {
    color: "inherit",
    url: "https://github.com/trending?l=SQLPL"
  },
  Squirrel: {
    color: "#800000",
    url: "https://github.com/trending?l=Squirrel"
  },
  "SRecode Template": {
    color: "#348a34",
    url: "https://github.com/trending?l=SRecode-Template"
  },
  Stan: {
    color: "#b2011d",
    url: "https://github.com/trending?l=Stan"
  },
  "Standard ML": {
    color: "#dc566d",
    url: "https://github.com/trending?l=Standard-ML"
  },
  Starlark: {
    color: "#76d275",
    url: "https://github.com/trending?l=Starlark"
  },
  Stata: {
    color: "inherit",
    url: "https://github.com/trending?l=Stata"
  },
  Stylus: {
    color: "#ff6347",
    url: "https://github.com/trending?l=Stylus"
  },
  SuperCollider: {
    color: "#46390b",
    url: "https://github.com/trending?l=SuperCollider"
  },
  Svelte: {
    color: "#ff3e00",
    url: "https://github.com/trending?l=Svelte"
  },
  SVG: {
    color: "#ff9900",
    url: "https://github.com/trending?l=SVG"
  },
  Swift: {
    color: "#ffac45",
    url: "https://github.com/trending?l=Swift"
  },
  SWIG: {
    color: "inherit",
    url: "https://github.com/trending?l=SWIG"
  },
  SystemVerilog: {
    color: "#DAE1C2",
    url: "https://github.com/trending?l=SystemVerilog"
  },
  Tcl: {
    color: "#e4cc98",
    url: "https://github.com/trending?l=Tcl"
  },
  Tcsh: {
    color: "inherit",
    url: "https://github.com/trending?l=Tcsh"
  },
  Terra: {
    color: "#00004c",
    url: "https://github.com/trending?l=Terra"
  },
  TeX: {
    color: "#3D6117",
    url: "https://github.com/trending?l=TeX"
  },
  Thrift: {
    color: "inherit",
    url: "https://github.com/trending?l=Thrift"
  },
  "TI Program": {
    color: "#A0AA87",
    url: "https://github.com/trending?l=TI-Program"
  },
  TLA: {
    color: "inherit",
    url: "https://github.com/trending?l=TLA"
  },
  TSQL: {
    color: "inherit",
    url: "https://github.com/trending?l=TSQL"
  },
  TSX: {
    color: "inherit",
    url: "https://github.com/trending?l=TSX"
  },
  Turing: {
    color: "#cf142b",
    url: "https://github.com/trending?l=Turing"
  },
  Twig: {
    color: "#c1d026",
    url: "https://github.com/trending?l=Twig"
  },
  TXL: {
    color: "inherit",
    url: "https://github.com/trending?l=TXL"
  },
  TypeScript: {
    color: "#2b7489",
    url: "https://github.com/trending?l=TypeScript"
  },
  "Unified Parallel C": {
    color: "#4e3617",
    url: "https://github.com/trending?l=Unified-Parallel-C"
  },
  "Unix Assembly": {
    color: "inherit",
    url: "https://github.com/trending?l=Unix-Assembly"
  },
  Uno: {
    color: "#9933cc",
    url: "https://github.com/trending?l=Uno"
  },
  UnrealScript: {
    color: "#a54c4d",
    url: "https://github.com/trending?l=UnrealScript"
  },
  UrWeb: {
    color: "inherit",
    url: "https://github.com/trending?l=UrWeb"
  },
  V: {
    color: "#4f87c4",
    url: "https://github.com/trending?l=V"
  },
  Vala: {
    color: "#fbe5cd",
    url: "https://github.com/trending?l=Vala"
  },
  VBA: {
    color: "#867db1",
    url: "https://github.com/trending?l=VBA"
  },
  VBScript: {
    color: "#15dcdc",
    url: "https://github.com/trending?l=VBScript"
  },
  VCL: {
    color: "#148AA8",
    url: "https://github.com/trending?l=VCL"
  },
  Verilog: {
    color: "#b2b7f8",
    url: "https://github.com/trending?l=Verilog"
  },
  VHDL: {
    color: "#adb2cb",
    url: "https://github.com/trending?l=VHDL"
  },
  "Vim script": {
    color: "#199f4b",
    url: "https://github.com/trending?l=Vim-script"
  },
  "Visual Basic .NET": {
    color: "#945db7",
    url: "https://github.com/trending?l=Visual-Basic-.NET"
  },
  Volt: {
    color: "#1F1F1F",
    url: "https://github.com/trending?l=Volt"
  },
  Vue: {
    color: "#2c3e50",
    url: "https://github.com/trending?l=Vue"
  },
  wdl: {
    color: "#42f1f4",
    url: "https://github.com/trending?l=wdl"
  },
  WebAssembly: {
    color: "#04133b",
    url: "https://github.com/trending?l=WebAssembly"
  },
  WebIDL: {
    color: "inherit",
    url: "https://github.com/trending?l=WebIDL"
  },
  wisp: {
    color: "#7582D1",
    url: "https://github.com/trending?l=wisp"
  },
  Wollok: {
    color: "#a23738",
    url: "https://github.com/trending?l=Wollok"
  },
  X10: {
    color: "#4B6BEF",
    url: "https://github.com/trending?l=X10"
  },
  xBase: {
    color: "#403a40",
    url: "https://github.com/trending?l=xBase"
  },
  XC: {
    color: "#99DA07",
    url: "https://github.com/trending?l=XC"
  },
  Xojo: {
    color: "inherit",
    url: "https://github.com/trending?l=Xojo"
  },
  XProc: {
    color: "inherit",
    url: "https://github.com/trending?l=XProc"
  },
  XQuery: {
    color: "#5232e7",
    url: "https://github.com/trending?l=XQuery"
  },
  XS: {
    color: "inherit",
    url: "https://github.com/trending?l=XS"
  },
  XSLT: {
    color: "#EB8CEB",
    url: "https://github.com/trending?l=XSLT"
  },
  Xtend: {
    color: "inherit",
    url: "https://github.com/trending?l=Xtend"
  },
  Yacc: {
    color: "#4B6C4B",
    url: "https://github.com/trending?l=Yacc"
  },
  YAML: {
    color: "#cb171e",
    url: "https://github.com/trending?l=YAML"
  },
  YARA: {
    color: "#220000",
    url: "https://github.com/trending?l=YARA"
  },
  YASnippet: {
    color: "#32AB90",
    url: "https://github.com/trending?l=YASnippet"
  },
  ZAP: {
    color: "#0d665e",
    url: "https://github.com/trending?l=ZAP"
  },
  Zeek: {
    color: "inherit",
    url: "https://github.com/trending?l=Zeek"
  },
  ZenScript: {
    color: "#00BCD1",
    url: "https://github.com/trending?l=ZenScript"
  },
  Zephir: {
    color: "#118f9e",
    url: "https://github.com/trending?l=Zephir"
  },
  Zig: {
    color: "#ec915c",
    url: "https://github.com/trending?l=Zig"
  },
  ZIL: {
    color: "#dc75e5",
    url: "https://github.com/trending?l=ZIL"
  },
  Zimpl: {
    color: "inherit",
    url: "https://github.com/trending?l=Zimpl"
  }
};

const useStyles$1 = makeStyles((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3),
    "& + .MuiAlert-root": {
      marginTop: theme.spacing(3)
    }
  },
  barContainer: {
    height: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: "4px",
    backgroundColor: "transparent",
    overflow: "hidden"
  },
  bar: {
    height: "100%",
    position: "relative"
  },
  languageDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    marginRight: theme.spacing(1),
    display: "inline-block"
  },
  label: {
    color: "inherit"
  }
}));
const LanguagesCard = () => {
  const { entity } = useEntity();
  let barWidth = 0;
  const classes = useStyles$1();
  const { owner, repo } = useProjectEntity(entity);
  const { value, loading, error } = useRequest(entity, "languages", 0, 0);
  const projectAlert = isGithubInsightsAvailable(entity);
  if (!projectAlert) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error", className: classes.infoCard }, error.message);
  }
  return value && Object.keys(value).length && owner && repo ? /* @__PURE__ */ React.createElement(InfoCard, { title: "Languages", className: classes.infoCard }, /* @__PURE__ */ React.createElement("div", { className: classes.barContainer }, Object.entries(value).map((language, index) => {
    var _a;
    barWidth = barWidth + language[1] / Object.values(value).reduce(
      (a, b) => a + b
    ) * 100;
    return /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        title: language[0],
        placement: "bottom-end",
        key: language[0]
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classes.bar,
          key: language[0],
          style: {
            marginTop: index === 0 ? "0" : `-16px`,
            zIndex: Object.keys(value).length - index,
            backgroundColor: ((_a = colors[language[0]]) == null ? void 0 : _a.color) || "#333",
            width: `${barWidth}%`
          }
        }
      )
    );
  })), Object.entries(value).map((language) => {
    var _a;
    return /* @__PURE__ */ React.createElement(
      Chip,
      {
        classes: {
          label: classes.label
        },
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
          "span",
          {
            className: classes.languageDot,
            style: {
              backgroundColor: ((_a = colors[language[0]]) == null ? void 0 : _a.color) || "#333"
            }
          }
        ), language[0], " -", " ", (language[1] / Object.values(value).reduce(
          (a, b) => a + b
        ) * 100).toFixed(2), "%"),
        variant: "outlined",
        key: language[0]
      }
    );
  })) : /* @__PURE__ */ React.createElement(React.Fragment, null);
};
var LanguagesCard$1 = LanguagesCard;

const useStyles = makeStyles((theme) => ({
  infoCard: {
    marginBottom: theme.spacing(3),
    "& + .MuiAlert-root": {
      marginTop: theme.spacing(3)
    },
    "& .MuiCardContent-root": {
      padding: theme.spacing(2, 1, 2, 2)
    }
  },
  readMe: {
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F5F5F5",
      borderRadius: "5px"
    },
    "&::-webkit-scrollbar": {
      width: "5px",
      backgroundColor: "#F5F5F5",
      borderRadius: "5px"
    },
    "&::-webkit-scrollbar-thumb": {
      border: "1px solid #555555",
      backgroundColor: "#555",
      borderRadius: "4px"
    }
  }
}));
const ReadMeCard = (props) => {
  const { entity } = useEntity();
  const { owner, repo, readmePath } = useProjectEntity(entity);
  const { hostname, baseUrl } = useEntityGithubScmIntegration(entity);
  const classes = useStyles();
  const linkPath = readmePath || "README.md";
  return /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: props.title || "Readme",
      className: classes.infoCard,
      deepLink: {
        link: `//${hostname}/${owner}/${repo}/blob/HEAD/${linkPath}`,
        title: "Readme",
        onClick: (e) => {
          e.preventDefault();
          window.open(`//${hostname}/${owner}/${repo}/blob/HEAD/${linkPath}`);
        }
      }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classes.readMe,
        style: {
          maxHeight: `${props.maxHeight}px`
        }
      },
      /* @__PURE__ */ React.createElement(
        MarkdownContent$1,
        {
          baseUrl,
          owner,
          repo,
          path: readmePath
        }
      )
    )
  );
};
var ReadMeCard$1 = ReadMeCard;

const getGithubClient = (apiHolder, errorApi) => {
  let githubClient = apiHolder.get(githubApiRef);
  if (!githubClient) {
    const auth = apiHolder.get(githubAuthApiRef);
    if (auth) {
      githubClient = new GithubClient({ githubAuthApi: auth, errorApi });
    }
  }
  if (!githubClient) {
    throw new Error(
      "The MarkdownCard component Failed to get the github client"
    );
  }
  return githubClient;
};
const GithubFileContent = (props) => {
  const { preserveHtmlComments } = props;
  const apiHolder = useApiHolder();
  const errorApi = useApi(errorApiRef);
  const { value, loading, error } = useAsync$1(async () => {
    const githubClient = getGithubClient(apiHolder, errorApi);
    return githubClient.getContent({ ...props });
  }, [apiHolder]);
  const transformImageUri = useCallback(
    (href) => {
      return (value == null ? void 0 : value.media[href]) || href;
    },
    [value == null ? void 0 : value.media]
  );
  const transformLinkUri = useCallback(
    (href) => {
      return (value == null ? void 0 : value.links[href]) || href;
    },
    [value == null ? void 0 : value.links]
  );
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert$1, { severity: "error" }, error.message);
  }
  if (!value) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  }
  let content = value.content;
  if (!preserveHtmlComments) {
    content = content.replace(/<!--.*?-->/g, "");
  }
  return /* @__PURE__ */ React.createElement(
    MarkdownContent$2,
    {
      transformImageUri,
      transformLinkUri,
      content
    }
  );
};
const GithubNotAuthorized = () => {
  const githubApi = useApi(githubAuthApiRef);
  return /* @__PURE__ */ React.createElement(Grid, { container: true }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 8 }, /* @__PURE__ */ React.createElement(Typography, null, "You are not logged into github. You need to be signed in to see the content of this card.")), /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 4, container: true, justifyContent: "flex-end" }, /* @__PURE__ */ React.createElement(Tooltip, { placement: "top", arrow: true, title: "Sign in to Github" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      variant: "outlined",
      color: "primary",
      onClick: () => githubApi.getAccessToken("repo")
    },
    "Sign in"
  ))));
};
const MarkdownContent = (props) => {
  const githubApi = useApi(githubAuthApiRef);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    const authSubscription = githubApi.sessionState$().subscribe((state) => {
      if (state === SessionState.SignedIn) {
        setIsLoggedIn(true);
      }
    });
    return () => {
      authSubscription.unsubscribe();
    };
  }, [githubApi]);
  return isLoggedIn ? /* @__PURE__ */ React.createElement(GithubFileContent, { ...props }) : /* @__PURE__ */ React.createElement(GithubNotAuthorized, null);
};
var MarkdownContent$1 = MarkdownContent;

const styles = makeStyles((theme) => ({
  card: {
    boxShadow: "none"
  },
  protectedBranches: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  infoCard: {
    marginBottom: theme.spacing(3),
    "& + .MuiAlert-root": {
      marginTop: theme.spacing(3)
    }
  },
  listStyle: {
    paddingTop: 0,
    "& :nth-child(1)": {
      paddingTop: "0.1rem"
    }
  },
  releaseTitle: {
    fontSize: "1.1rem",
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    marginRight: "0.5rem"
  },
  releaseTagIcon: {
    verticalAlign: "middle"
  },
  listItem: {
    justifyContent: "space-between"
  }
}));

const ReleasesCard = () => {
  const classes = styles();
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const { value, loading, error } = useRequest(entity, "releases", 0, 5);
  const { hostname } = useEntityGithubScmIntegration(entity);
  const projectAlert = isGithubInsightsAvailable(entity);
  if (!projectAlert) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error", className: classes.infoCard }, error.message);
  }
  return (value == null ? void 0 : value.length) && owner && repo ? /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "Releases",
      deepLink: {
        link: `//${hostname}/${owner}/${repo}/releases`,
        title: "Releases",
        onClick: (e) => {
          e.preventDefault();
          window.open(`//${hostname}/${owner}/${repo}/releases`);
        }
      },
      className: classes.infoCard
    },
    /* @__PURE__ */ React.createElement(List, null, value.map((release) => /* @__PURE__ */ React.createElement(ListItem, { className: classes.listItem, key: release.id }, /* @__PURE__ */ React.createElement(
      Link,
      {
        href: release.html_url,
        color: "inherit",
        target: "_blank",
        rel: "noopener noreferrer"
      },
      /* @__PURE__ */ React.createElement("p", { className: classes.releaseTitle }, release.name),
      /* @__PURE__ */ React.createElement(
        LocalOfferOutlinedIcon,
        {
          fontSize: "inherit",
          className: classes.releaseTagIcon
        }
      ),
      " ",
      release.tag_name
    ), release.prerelease && /* @__PURE__ */ React.createElement(Chip, { color: "primary", size: "small", label: "Pre-release" }))))
  ) : /* @__PURE__ */ React.createElement(React.Fragment, null);
};
var ReleasesCard$1 = ReleasesCard;

const NO_LICENSE_MSG = "No license file found";
const useProtectedBranches = (entity) => {
  const auth = useApi(githubAuthApiRef);
  const { baseUrl } = useEntityGithubScmIntegration(entity);
  const { owner, repo } = useProjectEntity(entity);
  const { state: branches, setState: setBranches } = useStore(
    (state) => state.branches
  );
  const { value, loading, error } = useAsync(async () => {
    var _a;
    let result;
    try {
      const token = await auth.getAccessToken(["repo"]);
      const octokit = new Octokit({ auth: token });
      const response = await octokit.request(
        "GET /repos/{owner}/{repo}/branches",
        {
          headers: {
            "if-none-match": branches.etag
          },
          baseUrl,
          owner,
          repo,
          protected: true
        }
      );
      result = { data: response.data, etag: (_a = response.headers.etag) != null ? _a : "" };
    } catch (e) {
      if (e.status === 304) {
        result = branches;
      }
    }
    return result;
  }, [baseUrl]);
  if (value) {
    setBranches(value);
  }
  return {
    branches: value ? value == null ? void 0 : value.data : void 0,
    loading,
    error
  };
};
const useRepoLicence = (entity) => {
  const auth = useApi(githubAuthApiRef);
  const { baseUrl } = useEntityGithubScmIntegration(entity);
  const { owner, repo } = useProjectEntity(entity);
  const { state: license, setState: setLicense } = useStore(
    (state) => state.license
  );
  const { value, loading, error } = useAsync(async () => {
    var _a;
    if (license.data === NO_LICENSE_MSG) {
      return license;
    }
    const token = await auth.getAccessToken(["repo"]);
    const octokit = new Octokit({ auth: token });
    let result;
    try {
      const response = await octokit.request(
        "GET /repos/{owner}/{repo}/contents/{path}",
        {
          headers: {
            "if-none-match": license.etag
          },
          baseUrl,
          owner,
          repo,
          path: "LICENSE"
        }
      );
      const licenseData = atob(response.data.content).split("\n").map((line) => line.trim()).filter(Boolean)[0];
      result = { etag: (_a = response.headers.etag) != null ? _a : "", data: licenseData };
    } catch (e) {
      if (e.status === 304) {
        return license;
      } else if (e.status === 404) {
        result = { etag: "", data: NO_LICENSE_MSG };
      }
    }
    return result;
  }, [baseUrl]);
  if (value) {
    setLicense(value);
  }
  return {
    license: value ? value == null ? void 0 : value.data : void 0,
    loading,
    error
  };
};

const ComplianceCard = () => {
  const { entity } = useEntity();
  const { branches, loading, error } = useProtectedBranches(entity);
  const {
    license,
    loading: licenseLoading,
    error: licenseError
  } = useRepoLicence(entity);
  const classes = styles();
  const { owner, repo } = useProjectEntity(entity);
  const projectAlert = isGithubInsightsAvailable(entity);
  if (!projectAlert) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
  }
  if (loading || licenseLoading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error || licenseError) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, "Error occured while fetching data for the compliance card:", " ", error == null ? void 0 : error.message);
  }
  return /* @__PURE__ */ React.createElement(InfoCard, { title: "Compliance report", className: classes.infoCard }, /* @__PURE__ */ React.createElement(
    StructuredMetadataTable,
    {
      metadata: {
        "Protected branches": (branches == null ? void 0 : branches.length) && owner && repo ? /* @__PURE__ */ React.createElement(List, { className: classes.listStyle }, branches.map((branch) => /* @__PURE__ */ React.createElement(ListItem, { key: branch.name }, branch.name))) : /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(
          WarningIcon,
          {
            style: {
              color: "orange",
              marginRight: "5px",
              flexShrink: 0
            }
          }
        ), /* @__PURE__ */ React.createElement("span", null, "None")),
        License: license === NO_LICENSE_MSG ? /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(
          WarningIcon,
          {
            style: {
              color: "orange",
              marginRight: "5px",
              flexShrink: 0
            }
          }
        ), /* @__PURE__ */ React.createElement("span", null, "None")) : license
      }
    }
  ));
};
var ComplianceCard$1 = ComplianceCard;

const EnvironmentsCard = () => {
  const classes = styles();
  const { entity } = useEntity();
  const { owner, repo } = useProjectEntity(entity);
  const { value, loading, error } = useRequest(entity, "environments", 0, 0);
  const { hostname } = useEntityGithubScmIntegration(entity);
  const projectAlert = isGithubInsightsAvailable(entity);
  if (!projectAlert) {
    return /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert$1, { severity: "error", className: classes.infoCard }, error.message);
  }
  return value.environments.length && owner && repo ? /* @__PURE__ */ React.createElement(
    InfoCard,
    {
      title: "Environments",
      deepLink: {
        link: `//${hostname}/${owner}/${repo}/deployments`,
        title: "Environments",
        onClick: (e) => {
          e.preventDefault();
          window.open(`//${hostname}/${owner}/${repo}/deployments`);
        }
      },
      className: classes.infoCard
    },
    /* @__PURE__ */ React.createElement(List, null, value.environments.map((environment) => /* @__PURE__ */ React.createElement(ListItem, { className: classes.listItem, key: environment.id }, /* @__PURE__ */ React.createElement(
      Link,
      {
        href: environment.html_url,
        color: "inherit",
        target: "_blank",
        rel: "noopener noreferrer"
      },
      /* @__PURE__ */ React.createElement("p", { className: classes.releaseTitle }, environment.name)
    ))))
  ) : /* @__PURE__ */ React.createElement(React.Fragment, null);
};
var EnvironmentsCard$1 = EnvironmentsCard;

const InsightsPage = () => {
  const { entity } = useEntity();
  return isGithubInsightsAvailable(entity) ? /* @__PURE__ */ React.createElement(Page, { themeId: "tool" }, /* @__PURE__ */ React.createElement(Content, null, /* @__PURE__ */ React.createElement(ContentHeader, { title: "GitHub Insights" }, /* @__PURE__ */ React.createElement(SupportButton, null, "Plugin to show GitHub Insights")), /* @__PURE__ */ React.createElement(Grid, { container: true, spacing: 3, direction: "row", alignItems: "stretch" }, /* @__PURE__ */ React.createElement(Grid, { item: true, sm: 12, md: 6, lg: 4 }, /* @__PURE__ */ React.createElement(ContributorsCard$1, null), /* @__PURE__ */ React.createElement(LanguagesCard$1, null), /* @__PURE__ */ React.createElement(ReleasesCard$1, null), /* @__PURE__ */ React.createElement(EnvironmentsCard$1, null), /* @__PURE__ */ React.createElement(ComplianceCard$1, null)), /* @__PURE__ */ React.createElement(Grid, { item: true, sm: 12, md: 6, lg: 8 }, /* @__PURE__ */ React.createElement(ReadMeCard$1, { maxHeight: 450 }))))) : /* @__PURE__ */ React.createElement(MissingAnnotationEmptyState, { annotation: GITHUB_INSIGHTS_ANNOTATION });
};

export { ComplianceCard$1 as ComplianceCard, ContributorsCard$1 as ContributorsCard, EntityGithubInsightsComplianceCard, EntityGithubInsightsContent, EntityGithubInsightsContributorsCard, EntityGithubInsightsEnvironmentsCard, EntityGithubInsightsLanguagesCard, EntityGithubInsightsReadmeCard, EntityGithubInsightsReleasesCard, EnvironmentsCard$1 as EnvironmentsCard, InsightsPage, LanguagesCard$1 as LanguagesCard, MarkdownContent$1 as MarkdownContent, ReadMeCard$1 as ReadMeCard, ReleasesCard$1 as ReleasesCard, githubInsightsPlugin, isGithubInsightsAvailable, githubInsightsPlugin as plugin, useStore };
//# sourceMappingURL=index.esm.js.map
